import React, { useState, useEffect } from "react";
import api from "../../../services/AxiosConnection";
import { FaTrash } from "react-icons/fa";
import useAuth from "../../../hooks/useAuth";

const SystemCalibration = () => {
  const { user, selectedSite } = useAuth();
  const isSuperAdmin = user?.role === "Super Admin";
  const [formData, setFormData] = useState({
    T1: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
    T2: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
    T3: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
    T4: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
    T5: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
    T6: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
    description: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasData, setHasData] = useState(false);
  const [dataId, setDataId] = useState(null);

  const fetchData = async () => {
    if (!selectedSite) {
      setIsLoading(false);
      setHasData(false);
      setFormData({
        T1: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
        T2: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
        T3: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
        T4: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
        T5: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
        T6: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
        description: "",
      });
      return;
    }

    try {
      setIsLoading(true);
      const response = await api.get(
        `/control-panel/getTemperatureCalibrationData?siteDb=${selectedSite}`
      );

      if (response.data.data && response.data.data.length > 0) {
        const data = response.data.data[0];

        const getValue = (value) => (value ? value.trim() : "");

        setFormData({
          T1: {
            minTemp: parseFloat(getValue(data.tempMin1)) || "",
            minPLC: parseInt(getValue(data.cntMin1), 10) || "",
            maxTemp: parseFloat(getValue(data.tempMax1)) || "",
            maxPLC: parseInt(getValue(data.cntMax1), 10) || "",
          },
          T2: {
            minTemp: parseFloat(getValue(data.tempMin2)) || "",
            minPLC: parseInt(getValue(data.cntMin2), 10) || "",
            maxTemp: parseFloat(getValue(data.tempMax2)) || "",
            maxPLC: parseInt(getValue(data.cntMax2), 10) || "",
          },
          T3: {
            minTemp: parseFloat(getValue(data.tempMin3)) || "",
            minPLC: parseInt(getValue(data.cntMin3), 10) || "",
            maxTemp: parseFloat(getValue(data.tempMax3)) || "",
            maxPLC: parseInt(getValue(data.cntMax3), 10) || "",
          },
          T4: {
            minTemp: parseFloat(getValue(data.tempMin4)) || "",
            minPLC: parseInt(getValue(data.cntMin4), 10) || "",
            maxTemp: parseFloat(getValue(data.tempMax4)) || "",
            maxPLC: parseInt(getValue(data.cntMax4), 10) || "",
          },
          T5: {
            minTemp: parseFloat(getValue(data.tempMin5)) || "",
            minPLC: parseInt(getValue(data.cntMin5), 10) || "",
            maxTemp: parseFloat(getValue(data.tempMax5)) || "",
            maxPLC: parseInt(getValue(data.cntMax5), 10) || "",
          },
          T6: {
            minTemp: parseFloat(getValue(data.tempMin6)) || "",
            minPLC: parseInt(getValue(data.cntMin6), 10) || "",
            maxTemp: parseFloat(getValue(data.tempMax6)) || "",
            maxPLC: parseInt(getValue(data.cntMax6), 10) || "",
          },
          description: getValue(data.nvcharDescription),
        });

        setDataId(data.ID);
        setHasData(true);
        if (isSuperAdmin && !response.data.data[0].ID) {
          setIsEditing(true); // Allow Super Admins to create new data
        }
      } else {
        setFormData({
          T1: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
          T2: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
          T3: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
          T4: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
          T5: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
          T6: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
          description: "",
        });
        setHasData(false);
        if (isSuperAdmin) {
          setIsEditing(true); // Allow Super Admins to create new data
        }
      }
    } catch (error) {
      console.error("Error fetching calibration data:", error);
      setFormData({
        T1: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
        T2: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
        T3: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
        T4: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
        T5: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
        T6: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
        description: "",
      });
      setHasData(false);
      if (isSuperAdmin) {
        setIsEditing(true); // Allow Super Admins to create new data
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    if (selectedSite && isMounted) {
      fetchData();
    }

    return () => {
      isMounted = false;
    };
  }, [selectedSite]);

  const handleInputChange = (box, field, value) => {
    if (!isSuperAdmin) return;
    setFormData((prev) => ({
      ...prev,
      [box]: { ...prev[box], [field]: value },
    }));
  };

  const handleDescriptionChange = (value) => {
    if (!isSuperAdmin) return;
    setFormData((prev) => ({ ...prev, description: value }));
  };

  const handleSave = async () => {
    if (!isSuperAdmin) return;
    try {
      setIsLoading(true);
      const payload = {
        siteDb: selectedSite,
        intSiteId: 1,
        tempMin1: formData.T1.minTemp,
        tempMax1: formData.T1.maxTemp,
        cntMin1: formData.T1.minPLC,
        cntMax1: formData.T1.maxPLC,
        tempMin2: formData.T2.minTemp,
        tempMax2: formData.T2.maxTemp,
        cntMin2: formData.T2.minPLC,
        cntMax2: formData.T2.maxPLC,
        tempMin3: formData.T3.minTemp,
        tempMax3: formData.T3.maxTemp,
        cntMin3: formData.T3.minPLC,
        cntMax3: formData.T3.maxPLC,
        tempMin4: formData.T4.minTemp,
        tempMax4: formData.T4.maxTemp,
        cntMin4: formData.T4.minPLC,
        cntMax4: formData.T4.maxPLC,
        tempMin5: formData.T5.minTemp,
        tempMax5: formData.T5.maxTemp,
        cntMin5: formData.T5.minPLC,
        cntMax5: formData.T5.maxPLC,
        tempMin6: formData.T6.minTemp,
        tempMax6: formData.T6.maxTemp,
        cntMin6: formData.T6.minPLC,
        cntMax6: formData.T6.maxPLC,
        nvcharDescription: formData.description,
      };

      let response;
      if (hasData && dataId) {
        response = await api.put("/control-panel/updateTemperatureCalibration", {
          ...payload,
          ID: dataId,
        });
      } else {
        response = await api.post("/control-panel/insertTemperatureCalibration", payload);
      }

      if (response.data?.ID) {
        setDataId(response.data.ID);
        setHasData(true);
      }

      alert("Data saved successfully!");
      setIsEditing(false);
      await fetchData();
    } catch (error) {
      console.error("Error saving calibration data:", error);
      alert("Failed to save calibration data.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!isSuperAdmin) return;
    if (!window.confirm("Are you sure you want to delete this data?")) return;

    try {
      setIsLoading(true);
      await api.delete("/control-panel/deleteTemperatureCalibration", {
        data: { siteDb: selectedSite, ID: dataId },
      });

      alert("Data deleted successfully!");
      setFormData({
        T1: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
        T2: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
        T3: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
        T4: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
        T5: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
        T6: { minTemp: "", minPLC: "", maxTemp: "", maxPLC: "" },
        description: "",
      });
      setHasData(false);
      setDataId(null);
      setIsEditing(false);
    } catch (error) {
      console.error("Error deleting calibration data:", error);
      alert("Failed to delete calibration data.");
    } finally {
      setIsLoading(false);
    }
  };

  const renderCalibrationBox = (box, title) => (
    <div className="bg-[#fdfdfd] p-4 rounded-lg shadow-md mb-4">
      <h4 className="text-lg font-medium text-gray-800 mb-2">{title}</h4>
      <div className="space-y-2">
        <div>
          <label className="block text-xs text-gray-700">
            Enter MP Min Temp ({title} Min Temp):
          </label>
          <input
            type="number"
            value={formData[box].minTemp}
            onChange={(e) => handleInputChange(box, "minTemp", e.target.value)}
            disabled={hasData ? !isEditing || !isSuperAdmin : !isSuperAdmin}
            className="mt-1 p-2 border rounded-md w-full focus:ring-blue-500 focus:border-blue-500 text-xs disabled:bg-gray-50 disabled:text-gray-500"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-700">
            Enter MP Min PLC Count (PLC cnt{title} Min):
          </label>
          <input
            type="number"
            value={formData[box].minPLC}
            onChange={(e) => handleInputChange(box, "minPLC", e.target.value)}
            disabled={hasData ? !isEditing || !isSuperAdmin : !isSuperAdmin}
            className="mt-1 p-2 border rounded-md w-full focus:ring-blue-500 focus:border-blue-500 text-xs disabled:bg-gray-50 disabled:text-gray-500"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-700">
            Enter MP Max Temp ({title} Max Temp):
          </label>
          <input
            type="number"
            value={formData[box].maxTemp}
            onChange={(e) => handleInputChange(box, "maxTemp", e.target.value)}
            disabled={hasData ? !isEditing || !isSuperAdmin : !isSuperAdmin}
            className="mt-1 p-2 border rounded-md w-full focus:ring-blue-500 focus:border-blue-500 text-xs disabled:bg-gray-50 disabled:text-gray-500"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-700">
            Enter MP Max PLC Count (PLC cnt{title} Max):
          </label>
          <input
            type="number"
            value={formData[box].maxPLC}
            onChange={(e) => handleInputChange(box, "maxPLC", e.target.value)}
            disabled={hasData ? !isEditing || !isSuperAdmin : !isSuperAdmin}
            className="mt-1 p-2 border rounded-md w-full focus:ring-blue-500 focus:border-blue-500 text-xs disabled:bg-gray-50 disabled:text-gray-500"
          />
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="text-center">
        <p className="text-gray-600 text-xs">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-blue-50 p-6 rounded-lg mb-6">
        <h2 className="text-lg font-bold text-blue-800 mb-2">
          System Calibration
        </h2>
        <p className="text-xs text-gray-600">
          {isSuperAdmin
            ? hasData
              ? "Edit calibration settings for Axle Box, Wheel, and Brake Disk temperatures."
              : "Configure calibration settings for Axle Box, Wheel, and Brake Disk temperatures."
            : hasData
            ? "View calibration settings for Axle Box, Wheel, and Brake Disk temperatures."
            : "No calibration data available."}
        </p>
      </div>

      {(!hasData && !isSuperAdmin) ? (
        <div className="text-center text-gray-500 text-sm">
          No calibration data available.
        </div>
      ) : (
        <>
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Axle Box Temp Calibration
            </h3>
            <div>
              {renderCalibrationBox("T1", "T1 - MP SIDE")}
              {renderCalibrationBox("T2", "T2 - AP SIDE")}
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Wheel Temp Calibration
            </h3>
            <div>
              {renderCalibrationBox("T3", "T3 - MP SIDE")}
              {renderCalibrationBox("T4", "T4 - AP SIDE")}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Brake Disk Temp Calibration
            </h3>
            <div>
              {renderCalibrationBox("T5", "T5 - MP SIDE")}
              {renderCalibrationBox("T6", "T6 - AP SIDE")}
            </div>
          </div>

          <div className="mb-6 bg-white shadow-md rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700">
              Description:
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleDescriptionChange(e.target.value)}
              disabled={hasData ? !isEditing || !isSuperAdmin : !isSuperAdmin}
              className="mt-1 p-2 border rounded-md w-full focus:ring-blue-500 focus:border-blue-500 text-xs disabled:bg-gray-50 disabled:text-gray-500"
            />
          </div>
        </>
      )}

      {isSuperAdmin && (
        <div className="flex justify-center gap-4">
          {hasData ? (
            <>
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-800 text-white font-medium rounded-lg transition text-xs"
              >
                {isEditing ? "Cancel" : "Edit"}
              </button>
              {isEditing && (
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-green-600 hover:bg-green-800 text-white font-medium rounded-lg transition text-xs"
                >
                  Save
                </button>
              )}
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-800 text-white font-medium rounded-lg transition text-xs flex items-center gap-1"
              >
                <FaTrash /> Delete
              </button>
            </>
          ) : (
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-green-600 hover:bg-green-800 text-white font-medium rounded-lg transition text-xs"
            >
              Save
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SystemCalibration;